import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@astryxdesign/core/Toast';
import type { ExportRequest } from '@qronstat/shared';
import { api } from '../api/client';

export interface ExportItem {
  id: string;
  title: string;
  format: 'xlsx' | 'csv' | 'pdf';
  createdAt: string;
  expiresAt: string;
  status: 'queued' | 'running' | 'done' | 'error';
  rows: number | null;
  size: number | null;
  finishedAt: string | null;
  failed: boolean;
}

interface ExportContextValue {
  start: (request: ExportRequest) => Promise<void>;
  download: (id: string) => void;
}

const ExportContext = createContext<ExportContextValue | null>(null);

export function downloadExport(id: string): void {
  const a = document.createElement('a');
  a.href = `/api/exports/${id}/file`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function downloadDataUrl(dataUrl: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const pollDelays = [800, 1200, 2000, 3000, 5000];

export function ExportProvider({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const toast = useToast();
  const client = useQueryClient();
  const active = useRef(new Set<string>());

  const poll = useCallback(
    async (id: string, title: string) => {
      active.current.add(id);
      let attempt = 0;
      const deadline = Date.now() + 10 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, pollDelays[Math.min(attempt, pollDelays.length - 1)]));
        attempt++;
        let item: ExportItem | undefined;
        try {
          const list = await api<{ items: ExportItem[] }>('/api/exports');
          item = list.items.find((i) => i.id === id);
        } catch {
          continue;
        }
        if (!item) break;
        if (item.status === 'done') {
          active.current.delete(id);
          void client.invalidateQueries({ queryKey: ['exports'] });
          downloadExport(id);
          toast({ body: intl.formatMessage({ id: 'export.ready' }, { title }), uniqueID: `export-${id}` });
          return;
        }
        if (item.status === 'error') break;
      }
      active.current.delete(id);
      void client.invalidateQueries({ queryKey: ['exports'] });
      toast({
        body: intl.formatMessage({ id: 'export.failed' }, { title }),
        type: 'error',
        uniqueID: `export-${id}`,
      });
    },
    [client, intl, toast],
  );

  const start = useCallback(
    async (request: ExportRequest) => {
      try {
        const res = await api<{ id: string }>('/api/exports', { method: 'POST', body: request });
        toast({ body: intl.formatMessage({ id: 'export.started' }), uniqueID: `export-${res.id}` });
        void poll(res.id, request.title);
      } catch {
        toast({ body: intl.formatMessage({ id: 'export.failedStart' }), type: 'error' });
      }
    },
    [intl, poll, toast],
  );

  const value = useMemo<ExportContextValue>(() => ({ start, download: downloadExport }), [start]);
  return <ExportContext.Provider value={value}>{children}</ExportContext.Provider>;
}

export function useExports(): ExportContextValue {
  const value = useContext(ExportContext);
  if (!value) throw new Error('ExportProvider missing');
  return value;
}
